import { reportException } from '_lib/utils/sentryService';

import { useCustomMutation, useCustomQuery } from '../apiResourceFactory';
import { API } from '../apiService';
import {
  AppConfigAiVoiceListDTO,
  AppConfigDetailDTO,
  AppConfigUpdateDTO,
  NationalNumberGroupsUpdateDTO,
  ServiceTitanConfigUpdateDTO
} from '../apiTypes.shim';
import { ourQueryClient } from '../QueryProvider';

export const appConfigResourceUri = `app-config`;

export const healthCheckResourceUri = `health-check`;

export const createAppConfigResource = () => {
  /** NOTE - this is a special GET endpoint that returns a single record, not a list! */
  const getAppConfig = async () => {
    const response = await API.get<AppConfigDetailDTO>(appConfigResourceUri, { extraOptions: { retry: 1 } });
    return response.data;
  };

  type UpdateAppConfigVariables =
    | { data: AppConfigUpdateDTO; url: '' }
    | { data: NationalNumberGroupsUpdateDTO; url: 'national-number-groups' }
    | { data: ServiceTitanConfigUpdateDTO; url: 'service-titan-config' };
  const useUpdateAppConfig = () => {
    const context = useCustomMutation<AppConfigDetailDTO, unknown, UpdateAppConfigVariables>({
      mutationFn: async ({ data, url }) => {
        const response = await API.put(`${appConfigResourceUri}/${url}`, data);
        return response.data;
      },
      onSuccess: (data) => {
        ourQueryClient.setQueryData([appConfigResourceUri], data);
      },
      onError: (error) => reportException(error)
    });

    return { ...context, put: context.mutateAsync };
  };

  const getAiVoiceConfig = async () => {
    const response = await API.get<AppConfigAiVoiceListDTO>(`${appConfigResourceUri}/ai-voices`, {
      extraOptions: { retry: 1 }
    });
    return response.data;
  };

  const getHealthCheck = async () => {
    const response = await API.get<{
      hostname: string;
      subdomains: string[];
      isFailover: boolean;
      clientIP: string;
      requestIp: string;
      requestIps: string[];
      uptime: number;
    }>(`health-check`, {
      extraOptions: { retry: 1 }
    });
    return response.data;
  };

  const getVersion = async () => {
    const response = await API.get<{ version: string }>(`version`, {
      extraOptions: { retry: 1 }
    });
    return response.data;
  };

  return {
    getAppConfig,
    useAppConfigQuery: () =>
      useCustomQuery({
        queryKey: [appConfigResourceUri],
        queryFn: getAppConfig
      }),
    useCachedAppConfigQuery: () =>
      useCustomQuery({
        queryKey: [appConfigResourceUri],
        queryFn: getAppConfig,
        refetchOnMount: false
      }),

    getAiVoiceConfig,
    useAiVoiceConfigQuery: () =>
      useCustomQuery({
        queryKey: [`${appConfigResourceUri}/ai-voices`],
        queryFn: getAiVoiceConfig
      }),
    getHealthCheck,
    useHealthCheckQuery: () =>
      useCustomQuery({
        queryKey: [healthCheckResourceUri],
        queryFn: getHealthCheck
      }),
    getVersion,
    useVersionQuery: () =>
      useCustomQuery({
        queryKey: [`version`],
        queryFn: getVersion
      }),
    useUpdateAppConfig
  };
};
